import { CircleCheck, CircleHelp, Loader2, Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PackagingShipmentOrgStats } from '@/shared/hooks/api/packagingShipments'
import { FunnelStatItem } from './FunnelStatItem'
import { formatStatFraction } from './packagingSupplierStats'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type ShipmentStatsRowProps = {
  stats: PackagingShipmentOrgStats
}

export function ShipmentStatsRow({ stats }: ShipmentStatsRowProps) {
  const { t } = useTranslation('packagingSuppliers')
  const checked = Math.max(0, stats.total - stats.updating)
  let checkedPercent = 0
  if (stats.total > 0) {
    checkedPercent = Math.round((checked / stats.total) * 100)
  }

  return (
    <div className='grid shrink-0 gap-2 sm:grid-cols-2 xl:grid-cols-4'>
      <Item variant='outline' size='xs' className='items-center'>
        <ItemMedia variant='icon' className='self-center'>
          <Package className='size-4 text-muted-foreground' aria-hidden />
        </ItemMedia>
        <ItemContent className='min-w-0'>
          <ItemTitle className='text-xs font-medium text-muted-foreground'>
            <span className='inline-flex items-center gap-1'>
              {t('shipmentsPage.stats.totalLabel')}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className='inline-flex text-muted-foreground/70 hover:text-muted-foreground'
                    aria-label={t('shipmentsPage.stats.totalTooltip')}
                  >
                    <CircleHelp className='size-3.5' aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-xs'>
                  {t('shipmentsPage.stats.totalTooltip')}
                </TooltipContent>
              </Tooltip>
            </span>
          </ItemTitle>
          <ItemDescription>
            {t('shipmentsPage.stats.totalDescription')}
          </ItemDescription>
        </ItemContent>
        <ItemActions className='shrink-0'>
          <span className='text-lg font-semibold tabular-nums'>
            {stats.total}
          </span>
        </ItemActions>
      </Item>
      <FunnelStatItem
        label={t('shipmentsPage.stats.checkedLabel')}
        tooltip={t('shipmentsPage.stats.checkedTooltip')}
        icon={
          <CircleCheck
            className='size-4 text-green-600 dark:text-green-500'
            aria-hidden
          />
        }
        percent={checkedPercent}
        description={formatStatFraction(checked, stats.total)}
      />
      <FunnelStatItem
        label={t('shipmentsPage.stats.updatingLabel')}
        tooltip={t('shipmentsPage.stats.updatingTooltip')}
        icon={
          <Loader2
            className='size-4 text-amber-600 dark:text-amber-500'
            aria-hidden
          />
        }
        percent={stats.updatingPercent}
        description={formatStatFraction(stats.updating, stats.total)}
      />
      <FunnelStatItem
        label={t('shipmentsPage.stats.awaitingFirstResultLabel')}
        tooltip={t('shipmentsPage.stats.awaitingFirstResultTooltip')}
        icon={
          <CircleHelp
            className='size-4 text-muted-foreground'
            aria-hidden
          />
        }
        percent={stats.awaitingFirstResultPercent}
        description={formatStatFraction(
          stats.awaitingFirstResult,
          stats.total
        )}
      />
    </div>
  )
}
